/**
 * format.ts —— UI 文案格式化
 *
 * 文案红线（设计方案 4.6）：任何与赢率相关的文案禁止出现
 * 「赢下本手概率 / 赢下底池概率 / 多人胜率 / 约 / ± / 采样 / 预计 / 建议加注 / 建议弃牌」。
 * 相关红线由 src/ui/__tests__ 的文案扫描用例守护。
 */

import type { ActionEffect, ActionType } from '../core/betting';
import type { Street } from '../core/equity';
import type { StyleKey } from '../core/ai';
import type { Player } from '../core/game';
import { STYLES } from '../core/ai/styles';

export const STREET_CN: Readonly<Record<Street, string>> = {
  preflop: '翻牌前',
  flop: '翻牌',
  turn: '转牌',
  river: '河牌',
};

export const ACTION_CN: Readonly<Record<ActionType, string>> = {
  fold: '弃牌',
  check: '过牌',
  call: '跟注',
  bet: '下注',
  raise: '加注',
  allin: '全下',
};

export function styleName(key: StyleKey): string {
  return STYLES[key].nameCn;
}

/**
 * 昵称拆分（Round21 T5 / AC-D2「身份唯一性优先于名字完整性」）。
 *
 * 同风格多个对手用「本名 + 空格 + 序号」区分（见 `assignNicknames`，如「闪电风暴 2」）。
 * 满桌窄档下昵称被 ellipsis 截断成「闪电风…」，序号被切掉 → 同名玩家无法区分（A-01 致命）。
 * 本函数把昵称拆成 `{ base, suffix }`：
 *   - `base`：可截断的主名（走 ellipsis）；
 *   - `suffix`：末尾序号（`#2`/`#3`…），渲染为独立元素、`flex:none`、**永不参与省略**。
 * 无序号（本名首次出现）时 `suffix` 为 `null`，渲染退化为纯 base。
 *
 * 纯函数、无 DOM，供 SeatCard 渲染与底部「等待 XX 行动」/结算/复盘的**唯一匹配**共用同源口径。
 */
export function splitNickname(name: string): { base: string; suffix: string | null } {
  const m = /^(.*\S)\s+(\d+)$/.exec(name);
  if (!m) return { base: name, suffix: null };
  return { base: m[1], suffix: `#${m[2]}` };
}

/** 千分位 + 等宽（渲染侧再加 .num class）。用于「筹码总额」等必须完整读数的场景（永不缩写）。 */
export function chips(n: number): string {
  return n.toLocaleString('zh-CN');
}

/**
 * 带符号盈亏（T8 · AC-N3）：结算/复盘统一口径。
 *   - net > 0 → `+78`；net < 0 → `−25`（真减号 U+2212，避免与连字符混淆）；net === 0 → `0`。
 * 纯函数、可单测，供结算文案 / 复盘条目 / 手牌历史同源使用。
 * 注：net===0 直接返回 `0`（不加正负号符号），既非「持平」误导，也避开文案红线对 `±` 的静态扫描。
 */
export function signedChips(net: number): string {
  if (net > 0) return `+${net}`;
  if (net < 0) return `−${-net}`;
  return '0';
}

/**
 * 结算「本手盈亏」文案（T8 · AC-N3）：**弃牌只输盲注的手不得显示「本手持平」**，一律显示实际盈亏。
 *   - net > 0 → `本手 +78`
 *   - net < 0 → `本手 −25`
 *   - net === 0 → `本手 0`（真正未盈亏；不再用「持平」误导为「摊牌打平」）
 * 纯函数、可单测。结算浮层与复盘条目共用此口径，保证「手牌历史条目盈亏与结算一致」（AC-N3）。
 */
export function handNetText(net: number): string {
  return `本手 ${signedChips(net)}`;
}

/**
 * 紧凑金额（偏差②）：下注 chip / 操作 pill 的额度在窄容器（chip max-width 56 / pill 72）里，
 * 大额千分位（如 12,345）会被 overflow 截断成「12,…」。这里对 ≥1000 的额度用 k/m/b 缩写，
 * 保证读数完整不省略（2000→2k、12345→12.3k、1500→1.5k、1_200_000→1.2m）；<1000 仍走千分位。
 * 仅用于 chip/pill 的额度展示，不影响筹码总额（chips）等必须逐位完整的场景。
 */
export function chipsShort(n: number): string {
  const abs = Math.abs(n);
  if (abs < 1000) return chips(n);
  const sign = n < 0 ? '-' : '';
  const units: readonly [number, string][] = [
    [1e9, 'b'],
    [1e6, 'm'],
    [1e3, 'k'],
  ];
  for (const [base, suffix] of units) {
    if (abs >= base) {
      const v = abs / base;
      // ≥100 个单位时去掉小数（如 250k），否则保留 1 位并去掉多余的 .0（2.0k→2k）。
      const s = v >= 100 ? Math.round(v).toString() : v.toFixed(1).replace(/\.0$/, '');
      return `${sign}${s}${suffix}`;
    }
  }
  return chips(n);
}

/** 赢率百分比：保留 1 位小数，绝不加「约 / ±」等近似字样（AC4.3）。 */
export function pct1(ratio: number): string {
  return `${(ratio * 100).toFixed(1)}%`;
}

export function pct(ratio: number, digits = 1): string {
  return `${(ratio * 100).toFixed(digits)}%`;
}

/**
 * 动作气泡文案。
 *
 * Round25 · M4 / C6（口径统一）：`call/bet/raise` 金额一律用**本街累计** `effect.to`（单一真源
 * = `streetContributed`），修正历史上 `call` 用本次增量 `effect.chips` 造成的口径漂移
 * （"先下盲注再跟注"只显示增量，看起来像"只显示本次操作金额"，根因 §7）。
 * `effect.to` 语义见 core/betting.ts：fold/check/call/bet/raise 后该座本街累计投入总额。
 * 标志：round25-street-contributed
 */
export function actionText(effect: ActionEffect): string {
  const base = ACTION_CN[effect.type];
  if (effect.allIn) return `全下 ${chips(effect.to)}`;
  switch (effect.type) {
    case 'fold':
    case 'check':
      return base;
    case 'call':
      return `跟注 ${chips(effect.to)}`;
    case 'bet':
      return `下注 ${chips(effect.to)}`;
    case 'raise':
      return `加注至 ${chips(effect.to)}`;
    default:
      return base;
  }
}

/**
 * FR-3 · AC3-3：把「最近动作」映射为操作 pill 文案 + 配色类（展示层，无内核改动）。
 *   fold→弃牌(灰) / check→过牌(中性青绿) / call→跟注(绿) / bet→下注(红粉) / raise→加注(红粉) / allin→全下(金)。
 * 全下判定优先：行动后已弃牌恒显「弃牌」，否则 stack===0（player.allIn）恒显「全下」（覆盖 call/raise 打光筹码）。
 * 金额取该座本街投入（有则附在文案后，如「加注 27」）；筹码为 0 的过牌/弃牌不带额。
 *
 * Round22 · F1（AC1）：由 Table.tsx 移至此处作为「动作+金额」pill 的单一真源，
 * 供对手 `.felt__actpill` 与英雄下注 pill（SeatCard `.bubble--pill`）复用同一文案/配色口径，
 * 保证英雄与对手 pill 文案格式 100% 一致（去筹码图标、无重复金额层）。
 */
export function pillInfo(kind: string, pl: Player): { text: string; cls: string } | null {
  if (pl.folded) return { text: '弃牌', cls: 'fold' };
  const amt = pl.streetContributed > 0 ? ` ${chipsShort(pl.streetContributed)}` : '';
  if (pl.allIn) return { text: `全下${amt}`, cls: 'allin' };
  switch (kind) {
    case 'fold':
      return { text: '弃牌', cls: 'fold' };
    case 'check':
      return { text: '过牌', cls: 'check' };
    case 'call':
      return { text: `跟注${amt}`, cls: 'call' };
    case 'bet':
      return { text: `下注${amt}`, cls: 'bet' };
    case 'raise':
      return { text: `加注${amt}`, cls: 'raise' };
    case 'allin':
      return { text: `全下${amt}`, cls: 'allin' };
    default:
      return null;
  }
}

export function relativeTime(ts: number, now = Date.now()): string {
  const diff = Math.max(0, now - ts);
  const min = Math.floor(diff / 60000);
  if (min < 1) return '刚刚';
  if (min < 60) return `${min} 分钟前`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h} 小时前`;
  return `${Math.floor(h / 24)} 天前`;
}
